"use client";

import { useMemo } from "react";

import type { QRProps } from "@/contexts/QREditorContext/types";
import type { QRSocialMedia } from "@/types/qr";
import { UseSocialDataResult, SocialTemplateTranslations } from "../types";
import { usePreviewData } from "@/features/previews/shared/hooks/usePreviewData";
import { optimizeCloudinaryImage } from "@/services/CloudinaryService";

/**
 * Resolves social media preview data from props with QREditorContext fallback,
 * social links validation, and optimized image handling.
 */
export const useSocialData = (props: QRProps<"social">): UseSocialDataResult => {
  const { data, isInitPreview: isInitialPreview } = usePreviewData(props);

  const translations = props.previewTranslations?.["social"] as SocialTemplateTranslations;

  const { socialLinks, imageSrc, hasValidSocials } = useMemo(() => {
    if (!data) {
      return {
        socialLinks: [],
        imageSrc: undefined,
        hasValidSocials: false,
      };
    }

    // Filter and validate social links
    const validSocialLinks = (data.links || []).filter((link: QRSocialMedia) => {
      if (!link?.url?.value) return false;

      // In initial preview, show all links (even with "#") to demonstrate UI
      if (isInitialPreview) return true;

      // In real mode, filter out empty or placeholder links
      return link.url.value !== "#";
    });

    // Process image source
    const processedImageSrc = data.logoUrl
      ? isInitialPreview
        ? data.logoUrl
        : optimizeCloudinaryImage({
            url: data.logoUrl,
            transformations: ["f_auto,q_auto,w_1280,h_640,dpr_auto,c_fill"],
          })
      : undefined;

    return {
      socialLinks: validSocialLinks,
      imageSrc: processedImageSrc,
      hasValidSocials: validSocialLinks.length > 0,
    };
  }, [data, isInitialPreview]);

  return {
    translations,
    isInitialPreview,
    data,
    socialLinks,
    imageSrc,
    hasValidSocials,
  };
};
